import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Route,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { Store } from "@ngrx/store";
import { Observable, of, Subscription, throwError } from "rxjs";
import {
  catchError,
  filter,
  map,
  switchMap,
  switchMapTo,
  take,
  tap,
} from "rxjs/operators";

import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ApplicationTheme } from "src/components/shared-components/enum/enumMisc";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { DpAuthenticationService } from "src/service/dp-authentication.service";
import { DpBuildingService } from "src/service/building.service";
import { EventService } from "src/service/event.service";
import { LoggerService } from "src/service/logger.service";
import { ThemeService } from "src/service/theme.service";
import { isHydrated } from "src/state/hydrate";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { AuthenticationService } from "../service/authentication.service";
import { environment } from "src/environments/environment";

@Injectable({ providedIn: "root" })

/**
 *
 *  La guardia {@link AuthGuard} controlla l'accesso alle pagine del sistema
 *
 */
export class AuthGuard {
  private _logContext: string = "Auth-guard";
  public _eventSubscriptions: Array<Subscription> = new Array<Subscription>();

  constructor(
    private _authService: AuthenticationService,
    private _loggerService: LoggerService,
    private _eventService: EventService,
    private _store: Store,
    private _themeService: ThemeService,
    private _dpAuthService: DpAuthenticationService,
    private _dpBuildingService: DpBuildingService
  ) {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "CONSTRUCTOR",
      " GUARD BUILD "
    );
  }

  /**
   * Il metodo {@link canActivate} esegue il costrutto di protezione alle pagine: se l'utente è autenticato
   * puo caricare la pagina altrimenti viene rediretto alla login
   *
   * @param route : parametro di default passato al canActivate
   *
   * @param state : parametro di default passato al canActivate
   */
  canLoad(
    route: Route
  ):
    | boolean
    | UrlTree
    | Promise<boolean | UrlTree>
    | Observable<boolean | UrlTree> {
    return this.checkIsStoreReady().pipe(
      switchMap(() => {
        return this.canUserLoadOrActivateRoute();
      })
    );
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | boolean
    | UrlTree
    | Promise<boolean | UrlTree>
    | Observable<boolean | UrlTree> {
    if (state.url == "/reserved-area/building-list") {
      this._dpBuildingService.deselectCurrentBuilding();
    }

    return this.checkIsStoreReady().pipe(
      switchMap(() => {
        return this.canUserLoadOrActivateRoute();
      })
    );
  }

  /**
   * Handle if user could load or activate request root //
   *
   * @returns
   */
  private canUserLoadOrActivateRoute(): Observable<boolean> {
    return this._store.select(currentUserData).pipe(
      map((currentUserData: GenericUserData) => currentUserData?.currentUser),
      switchMap((currentUser: CurrentSessionData) => {
        const isRefreshTokenValid =
          this._authService.isRefreshTokenValid(currentUser);

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_authService.userCredentials",
          "canActivate()",
          " | RefreshToken Valid: " + isRefreshTokenValid
        );

        /**
         * Check if only refresh token is valid, because access should be refreshed by authentication interceptor
         */
        return of(isRefreshTokenValid);
      }),
      switchMap((resultCheckTokens: boolean) => {
        if (resultCheckTokens) {
          return of(true);
        } else {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "canActivate()",
            "Refresh token is NOT valid. Executing LOGOUT"
          );

          this._dpAuthService.logout();
          // if (this._themeService.isDigitalPlace) {
          //   this._dpAuthService.logout();
          // } else {
          //   this._authService.logout(false);
          // }

          return of(false);
        }
      }),
      catchError((error) => {
        this._loggerService.log(
          LogLevel.LOG_ERROR,
          this._logContext,
          this._logContext,
          "canActivate()",
          "Error selecting current user from store. Executing LOGOUT: " +
            (error?.message ?? JSON.stringify(error))
        );
        this._authService.logout();

        return throwError(error);
      })
    );
  }

  /**
   *   This method check if the NgRx store has already been initialised (IE. hydrated) from the local storage.
   * It does so observing the value of the "hydrated" feature.
   * @returns An observable. This Observable shall not emit until the "hydrated" feature changes its value to TRUE.
   */
  private checkIsStoreReady(): Observable<boolean> {
    return this._store.select(isHydrated).pipe(
      tap((isHydrated: boolean) => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          " checkIsStoreReady() ",
          "isHydrated: " + isHydrated
        );
      }),
      filter((isHydrated: boolean) => isHydrated),
      tap((isHydrated: boolean) => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          " checkIsStoreReady() ",
          "GO AHEAD!"
        );
      }),
      take(1)
    );
  }
}
